import React, { useState, useEffect, useCallback } from "react";
import { Select, Typography } from "antd";
import { MailOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
  getGroupOwnMemberships,
  patchGroupIdentityMembership,
  getIdentity,
} from "../../actions/api";
import { errorAction, types } from "@authzsvc/api-lib";
import { showErrorNotification, showSuccessNotification } from "../../util";

/**
 * Email address selector for group membership notifications.
 *
 * Allows users to select which email address should receive
 * notifications for their membership in a specific group. The component fetches the
 * user's current membership data and displays available email addresses in a dropdown.
 *
 * Available email addresses include:
 * - Primary account email (default if no override is set)
 * - External email (if configured)
 * - All emails from accounts owned by the user
 *
 * When a user selects an email, it updates their mailing address preference for this
 * specific group membership. CERN emails (@cern.ch) are stored as null to use the
 * default primary account email.
 *
 * @param {Object} props - Component props
 * @param {string} props.groupId - The ID of the group
 * @param {boolean} [props.disabled=false] - Whether the component is disabled
 * @returns {JSX.Element} The email selector component
 */

const MailingAddressSelector = ({ groupId, disabled = false }) => {
  const [availableEmails, setAvailableEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentUserMembership, setCurrentUserMembership] = useState(null);
  const [fetchedIdentity, setFetchedIdentity] = useState(null);

  const identity = useSelector((state) => state.identity);
  const identityAccounts = useSelector((state) => state.identityAccounts);
  const dispatch = useDispatch();

  // Helper function to show error notifications
  const showError = (message = "Please try again later") => {
    showErrorNotification("Failed to update email address", message);
  };

  // Collect emails from three sources: primary account, external email, and identity accounts
  const collectAvailableEmails = useCallback(() => {
    const emailSet = new Set();

    // Prefer fetchedIdentity (requested with explicit fields) when available
    const currentIdentity = fetchedIdentity || identity || {};

    // Primary email: either from identity or match UPN in accounts
    const primaryAccountEmail =
      currentIdentity?.primaryAccountEmail ||
      identityAccounts?.find(
        (account) => account.uniqueIdentifier === currentIdentity?.upn
      )?.emailAddress;

    // Add emails in priority order - Set automatically deduplicates
    [
      primaryAccountEmail, // 1. Primary account email
      currentIdentity?.externalEmail, // 2. External email
      ...(identityAccounts // 3. All emails from identity accounts
        ?.map((account) => account.emailAddress)
        .filter(Boolean) || []),
    ].forEach((email) => email && emailSet.add(email));

    return Array.from(emailSet);
  }, [identity, identityAccounts, fetchedIdentity]);

  const fetchCurrentUserMembership = useCallback(async () => {
    if (!groupId) return;

    setLoading(true);

    const response = await dispatch(getGroupOwnMemberships(groupId));

    if (response?.type === errorAction(types.GET_GROUP_OWN_MEMBERSHIPS)) {
      setCurrentUserMembership(null);
    } else if (response?.payload?.data) {
      setCurrentUserMembership(response.payload.data);
    }

    setLoading(false);
  }, [dispatch, groupId]);

  useEffect(() => {
    fetchCurrentUserMembership();
  }, [fetchCurrentUserMembership]);

  useEffect(() => {
    if (
      (fetchedIdentity && Object.keys(fetchedIdentity).length > 0) ||
      (identity && Object.keys(identity).length > 0)
    ) {
      setAvailableEmails(collectAvailableEmails());
    }
  }, [collectAvailableEmails, identity, fetchedIdentity]);

  // Fetch the current user's identity with explicit fields to ensure externalEmail is returned
  useEffect(() => {
    const fetchDetailedIdentity = async () => {
      const resp = await dispatch(
        getIdentity({
          queryFilter: {
            field: ["externalEmail", "primaryAccountEmail", "upn"],
          },
        })
      );

      if (resp?.type === errorAction(types.GET_IDENTITY)) {
        // Keep fetchedIdentity null — follow existing error handling pattern
        return;
      }

      if (resp?.payload?.data) {
        setFetchedIdentity(resp.payload.data);
      }
    };

    fetchDetailedIdentity();
  }, [currentUserMembership, dispatch]);

  // Current selected email: prioritize emailAddress over memberIdentity.email
  const selectedValue =
    currentUserMembership?.emailAddress ||
    currentUserMembership?.memberIdentity?.email ||
    "";

  const handleChange = async (newMailingAddress) => {
    const userIdentityId = currentUserMembership?.memberIdentity?.id;

    if (!userIdentityId) {
      showError("Unable to update mailing address.");
      return;
    }

    setLoading(true);

    // CERN emails set to null will default to memberIdentity.email
    const emailToUpdate = newMailingAddress?.endsWith("@cern.ch")
      ? null
      : newMailingAddress;

    const response = await dispatch(
      patchGroupIdentityMembership(groupId, userIdentityId, [
        {
          op: "replace",
          path: "/emailAddress",
          value: emailToUpdate,
        },
      ])
    );

    if (response.type === errorAction(types.PATCH_GROUP_IDENTITY_MEMBERSHIP)) {
      showError(response.payload?.data?.message);
    } else {
      showSuccessNotification("", "Email address updated successfully");
      await fetchCurrentUserMembership();
    }

    setLoading(false);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Typography.Text style={{ whiteSpace: "nowrap" }}>
        My mailing address:
      </Typography.Text>
      <Select
        value={selectedValue}
        onChange={handleChange}
        disabled={disabled || loading}
        loading={loading}
        placeholder="Select email address"
        suffixIcon={<MailOutlined style={{ pointerEvents: "none" }} />}
        popupMatchSelectWidth={false}
        style={{ maxWidth: 300 }}
        title={selectedValue || undefined}
        optionRender={(option) => (
          <span title={option.value}>{option.value}</span>
        )}
      >
        {availableEmails.map((email) => (
          <Select.Option key={email} value={email}>
            {email}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

MailingAddressSelector.propTypes = {
  groupId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

export default MailingAddressSelector;
